import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";

export interface CompanyBiometricDeviceRecord {
  id: ID;
  companyId: ID;
  warehouseId: string | null;
  biometricDeviceId: ID;
  serialNumber: string | null;
  ipAddress: string;
  port: number;
  communicationPassword: string | null;
  location: string | null;
  lastConnectedAt: string | null;
  attendanceLastSyncedAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  companyName: string | null;
  warehouseName: string | null;
  biometricModel: string | null;
  biometricDriver: string | null;
  biometricProtocol: string | null;
}

export const INVENTORY_COMPANY_BIOMETRIC_DEVICES_LS_KEY = "inventory.companyBiometricDevices.v1";
const LS_KEY = INVENTORY_COMPANY_BIOMETRIC_DEVICES_LS_KEY;

type CompanyBiometricDeviceApiRecord = {
  id: number | string;
  company_id: number | string;
  warehouse_id?: number | string | null;
  biometric_device_id: number | string;
  serial_number?: string | null;
  ip_address: string;
  port?: number | string | null;
  communication_password?: string | null;
  location?: string | null;
  last_connected_at?: string | null;
  attendance_last_synced_at?: string | null;
  is_active?: boolean | number | null;
  created_at?: string;
  updated_at?: string;
  company?: { id?: number | string; name?: string | null } | null;
  warehouse?: { id?: number | string; name?: string | null } | null;
  biometric_device?: {
    id?: number | string;
    model?: string | null;
    driver?: string | null;
    protocol?: string | null;
  } | null;
};

type ListApiResponse = {
  data?: CompanyBiometricDeviceApiRecord[];
  meta?: { page?: number; per_page?: number; total?: number };
};

type UpsertApiResponse = { message?: string; data?: CompanyBiometricDeviceApiRecord };
type DeleteApiResponse = { message?: string };
type ShowApiResponse = { data?: CompanyBiometricDeviceApiRecord };

export type CompanyBiometricDeviceUpsertPayload = {
  companyId: ID;
  warehouseId?: ID | null;
  biometricDeviceId: ID;
  serialNumber?: string | null;
  ipAddress: string;
  port?: number;
  communicationPassword?: string | null;
  location?: string | null;
  isActive?: boolean;
};

function mapApiRecord(r: CompanyBiometricDeviceApiRecord): CompanyBiometricDeviceRecord {
  const portRaw = Number(r.port ?? 4370);
  const port = Number.isFinite(portRaw) && portRaw >= 1 && portRaw <= 65535 ? portRaw : 4370;

  return {
    id: String(r.id),
    companyId: String(r.company_id),
    warehouseId: r.warehouse_id != null && String(r.warehouse_id) !== "" ? String(r.warehouse_id) : null,
    biometricDeviceId: String(r.biometric_device_id),
    serialNumber: r.serial_number?.trim() ? r.serial_number.trim() : null,
    ipAddress: (r.ip_address ?? "").trim(),
    port,
    communicationPassword: r.communication_password?.trim() ? r.communication_password.trim() : null,
    location: r.location?.trim() ? r.location.trim() : null,
    lastConnectedAt: r.last_connected_at ?? null,
    attendanceLastSyncedAt: r.attendance_last_synced_at ?? null,
    isActive: r.is_active === false || r.is_active === 0 ? false : true,
    createdAt: r.created_at || new Date().toISOString(),
    updatedAt: r.updated_at || r.created_at || new Date().toISOString(),
    companyName: r.company?.name?.trim() ? r.company.name.trim() : null,
    warehouseName: r.warehouse?.name?.trim() ? r.warehouse.name.trim() : null,
    biometricModel: r.biometric_device?.model?.trim() ? r.biometric_device.model.trim() : null,
    biometricDriver: r.biometric_device?.driver?.trim() ? r.biometric_device.driver.trim() : null,
    biometricProtocol: r.biometric_device?.protocol?.trim() ? r.biometric_device.protocol.trim() : null,
  };
}

function dedupe(items: CompanyBiometricDeviceRecord[]): CompanyBiometricDeviceRecord[] {
  const m = new Map<ID, CompanyBiometricDeviceRecord>();
  for (const x of items) m.set(x.id, x);
  return [...m.values()];
}

function load(): CompanyBiometricDeviceRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw) as CompanyBiometricDeviceRecord[];
    return Array.isArray(p) ? dedupe(p) : [];
  } catch {
    return [];
  }
}

function save(items: CompanyBiometricDeviceRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupe(items)));
  window.dispatchEvent(new CustomEvent("inventory_company_biometric_devices_cache_changed"));
}

export function listCompanyBiometricDeviceRecords(): CompanyBiometricDeviceRecord[] {
  return load();
}

export function getCompanyBiometricDeviceById(id: ID): CompanyBiometricDeviceRecord | undefined {
  return load().find((r) => String(r.id) === String(id));
}

function toApiPayload(p: CompanyBiometricDeviceUpsertPayload) {
  return {
    company_id: Number(p.companyId),
    warehouse_id: p.warehouseId != null && String(p.warehouseId).trim() !== "" ? Number(p.warehouseId) : null,
    biometric_device_id: Number(p.biometricDeviceId),
    serial_number: p.serialNumber?.trim() || null,
    ip_address: p.ipAddress.trim(),
    port: p.port ?? 4370,
    communication_password: p.communicationPassword?.trim() || null,
    location: p.location?.trim() || null,
    is_active: p.isActive !== false,
  };
}

export async function syncCompanyBiometricDevicesFromApi(): Promise<CompanyBiometricDeviceRecord[]> {
  const response = await api.get<ListApiResponse>("/api/v1/company-biometric-devices", { params: { per_page: 200 } });
  const list = (response.data?.data ?? []).map(mapApiRecord);
  save(list);
  return listCompanyBiometricDeviceRecords();
}

export async function fetchCompanyBiometricDeviceById(id: ID): Promise<CompanyBiometricDeviceRecord> {
  const res = await api.get<ShowApiResponse>(`/api/v1/company-biometric-devices/${id}`);
  const row = res.data?.data;
  if (!row) throw new Error("Invalid company biometric device response.");
  const record = mapApiRecord(row);
  const items = load();
  const i = items.findIndex((r) => String(r.id) === String(record.id));
  if (i >= 0) items[i] = record;
  else items.push(record);
  save(items);
  return record;
}

export async function createCompanyBiometricDevice(payload: CompanyBiometricDeviceUpsertPayload) {
  const res = await api.post<UpsertApiResponse>("/api/v1/company-biometric-devices", toApiPayload(payload));
  const row = res.data?.data;
  if (!row) throw new Error("Invalid create company biometric device response.");
  const record = mapApiRecord(row);
  const items = load();
  items.push(record);
  save(items);
  return { message: res.data?.message ?? "Company biometric device created.", record };
}

export async function updateCompanyBiometricDevice(id: ID, payload: CompanyBiometricDeviceUpsertPayload) {
  const res = await api.put<UpsertApiResponse>(`/api/v1/company-biometric-devices/${id}`, toApiPayload(payload));
  const row = res.data?.data;
  if (!row) throw new Error("Invalid update company biometric device response.");
  const record = mapApiRecord(row);
  const items = load();
  const i = items.findIndex((r) => String(r.id) === String(record.id));
  if (i >= 0) items[i] = record;
  else items.push(record);
  save(items);
  return { message: res.data?.message ?? "Company biometric device updated.", record };
}

export async function deleteCompanyBiometricDevice(id: ID) {
  const res = await api.delete<DeleteApiResponse>(`/api/v1/company-biometric-devices/${id}`);
  const items = load().filter((r) => String(r.id) !== String(id));
  save(items);
  return { message: res.data?.message ?? "Company biometric device deleted." };
}

export function companyBiometricDeviceDisplayName(r: CompanyBiometricDeviceRecord): string {
  return r.biometricModel?.trim() || r.ipAddress || `Device #${r.id}`;
}
